import { ScrollView, View } from "react-native";
import { FormControlledResidentMultiSelect } from "ui/molecules/form/FormControlledResidentMultiSelect";
import {
  dateGreaterThanValidation,
  minDateValidation,
  requiredValidation,
} from "ui/utils/Validators";
import {
  InitialTimeInterface,
  useHandleAppointments,
} from "../hooks/useHandleAppointments";
import { AppointmentTypeEnums } from "app/services/api/types/calendar/enums/AppointmentTypeEnums";
import { FormControlledDropdown } from "ui/molecules/form/FormControlledDropdown";
import { FormControlledDatePicker } from "ui/molecules/form/FormControlledDatePicker";
import { FormControlledTimePicker } from "ui/molecules/form/FormControlledTimePicker";
import { CreateCalendarEventForm } from "app/services/api/types/calendar/requests/CreateCalendarEventForm";
import { FormRow } from "ui/organisms/FormRow";
import { RecurrencePattern } from "../organisms/RecurrencePattern";
import { EventHeader } from "../organisms/EventHeader";
import { AdditionalInformation } from "../organisms/AdditionalInformation";
import { EventEndDetails } from "../organisms/EventEndDetails";
import { NotesSection } from "../organisms/NotesSection";
import { FormControlledInputTextArea } from "ui/molecules/form/FormControlledInputTextArea";
import { useHandleAppointmentForm } from "../hooks/useHandleAppointmentForm";
import { Controller } from "react-hook-form";
import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from "ui/gluestack/checkbox";
import { CheckIcon } from "ui/gluestack/icon";
import { AppointmentModel } from "app/services/api/types/calendar/models/AppointmentModel";
import FormControlledLocationSelector from "app/components/molecules/FormControlledLocationSelector";

interface Props {
  initialAppointment?: AppointmentModel;
  initialTimes?: InitialTimeInterface;
  onCancel: () => void;
  onClose: () => void;
}

export default function CalendarEventForm({
  initialAppointment,
  initialTimes,
  onCancel,
  onClose,
}: Props) {
  const {
    control,
    selectedType,
    shouldShowEndDetails,
    allResidentsSelected,
    submit,
    watch,
    isSubmitting,
    locationSelected,
  } = useHandleAppointments({
    initialAppointment,
    initialTimes,
    onCreateSuccess: onClose,
  });

  const {
    locationTypeOptions,
    typeOptions,
    areEventTypesLoading,
    isEventTypesError,
  } = useHandleAppointmentForm();

  return (
    <View className="flex-1">
      <EventHeader
        control={control}
        showCancel={!!initialAppointment}
        onSubmit={submit}
        onCancel={onCancel}
        onClose={onClose}
        isSubmitting={isSubmitting}
      />
      <ScrollView showsVerticalScrollIndicator={false}>
        <FormRow label="Location">
          <FormControlledLocationSelector
            control={control}
            name="location_id"
          />
        </FormRow>
        <FormRow label="Resident(s)">
          <FormControlledResidentMultiSelect<CreateCalendarEventForm>
            name="participant_ids"
            control={control}
            label=""
            locationId={locationSelected}
            isDisabled={allResidentsSelected}
            isRequired
            rules={{
              validate: {
                requiredIf: (value) => {
                  if (!allResidentsSelected && !value) {
                    return "Residents are required";
                  }
                  return true;
                },
              },
            }}
          />
          <Controller
            control={control}
            name="all_residents"
            render={({ field: { value, onChange } }) => (
              <Checkbox
                isChecked={!!value}
                onChange={(checked: boolean) => onChange(checked)}
                size="sm"
                className="mt-4"
                value={value ? "checked" : "unchecked"}
              >
                <CheckboxIndicator>
                  <CheckboxIcon as={CheckIcon} />
                </CheckboxIndicator>
                <CheckboxLabel>Select all residents</CheckboxLabel>
              </Checkbox>
            )}
          />
        </FormRow>
        <FormRow label="Appointment type" className="gap-4">
          <FormControlledDropdown<CreateCalendarEventForm>
            name="type"
            control={control}
            placeholder="Choose type"
            options={typeOptions}
            isDisabled={
              areEventTypesLoading || isSubmitting || isEventTypesError
            }
            rules={{
              required: requiredValidation("Appointment type"),
            }}
          />
        </FormRow>
        <FormRow className="gap-4" isFullWidth>
          <FormControlledInputTextArea<CreateCalendarEventForm>
            id="type_specification"
            name="type_specification"
            control={control}
            label="Note"
            placeholder="Enter any further information here"
            rules={{
              validate: {
                requiredIf: (value) => {
                  if (selectedType === AppointmentTypeEnums.OTHER && !value) {
                    return "Specification is required for 'Other' type";
                  }
                  return true;
                },
              },
            }}
          />
        </FormRow>
        <FormRow label="Location Type">
          <FormControlledDropdown<CreateCalendarEventForm>
            name="location_type"
            control={control}
            placeholder="Choose location type"
            options={locationTypeOptions}
            isRequired
            rules={{
              required: requiredValidation("Location type"),
            }}
          />
        </FormRow>
        <FormRow label="Location Description" isFullWidth>
          <FormControlledInputTextArea<CreateCalendarEventForm>
            id="location_description"
            name="location_description"
            control={control}
            placeholder="Enter description"
          />
        </FormRow>

        <FormRow label="Starts At" className="md:flex-row">
          <View className="md:w-52 ">
            <FormControlledDatePicker<CreateCalendarEventForm>
              id="start_date"
              name="start_date"
              control={control}
              placeholder={"00/00/0000"}
              isRequired
              rules={{
                required: requiredValidation("Start date"),
                validate: {
                  minDate: minDateValidation(
                    new Date(),
                    "Start date must be today or a future date",
                  ),
                },
              }}
            />
          </View>
          <FormControlledTimePicker<CreateCalendarEventForm>
            id="start_time"
            name="start_time"
            control={control}
            placeholder="00:00 AM"
            isRequired
            className="md:ml-5 mt-2 md:mt-0"
            rules={{
              required: requiredValidation("Start time"),
            }}
          />
        </FormRow>
        <FormRow label="Ends At" className="md:flex-row ">
          <View className="md:w-52 ">
            <FormControlledDatePicker<CreateCalendarEventForm>
              id="end_date"
              name="end_date"
              control={control}
              placeholder={"00/00/0000"}
              isRequired
              rules={{
                required: requiredValidation("End date"),
                validate: {
                  dateGreaterThanValidation: dateGreaterThanValidation(
                    () => watch(`start_date`),
                    "Start Date",
                    "Repeat Until Date",
                  ),
                },
              }}
            />
          </View>
          <FormControlledTimePicker<CreateCalendarEventForm>
            id="end_time"
            name="end_time"
            control={control}
            placeholder="00:00 AM"
            className="md:ml-5 mt-2 md:mt-0"
            isRequired
            rules={{
              required: requiredValidation("End time"),
            }}
          />
        </FormRow>

        <View className="border border-gray-200 w-full mt-4 mb-8" />
        <AdditionalInformation control={control} />
        <View className="border border-gray-200 w-full mt-4 mb-8" />
        <RecurrencePattern control={control} />
        {shouldShowEndDetails ? <EventEndDetails control={control} /> : null}
        <View className="border border-gray-200 w-full my-8" />
        <NotesSection control={control} />
      </ScrollView>
    </View>
  );
}
